import { useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  Row,
  Col,
  Card,
  Typography,
  message,
  Spin,
  Popconfirm,
  Tooltip,
  Grid,
  List,
  Input,
  Select
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { TableProps } from 'antd';
import { getLeaveRequests, deleteLeaveRequest } from '@/api/employees';
import { useAuth } from '@/contexts/AuthContext';
import { LeaveRequestModal } from '@/components/features/employee/LeaveRequestModal';
import type { LeaveRequest } from '@/types/employee/attendance';

const { Title } = Typography;
const { useBreakpoint } = Grid;

export default function LeaveRequests() {
  const { t } = useTranslation(['attendance', 'common', 'employees']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  
  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState<LeaveRequest | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Queries
  const { data: leaveRequestsData = { requests: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['leaveRequests', pagination.current, pagination.pageSize, searchTerm, selectedStatus, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getLeaveRequests({ 
        firestoreUser,
        status: selectedStatus || undefined,
        pageSize: pagination.pageSize,
        page: pagination.current,
        searchTerm
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteLeaveRequest(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('attendance:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
    },
    onError: (error: Error) => {
      message.error(error.message || t('attendance:messages.deleteError'));
    }
  });

  const getStatusColor = (status: LeaveRequest['status']) => {
    switch (status) {
      case 'Pending': return 'processing';
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type: LeaveRequest['type']) => {
    switch (type) {
      case 'Annual': return 'blue';
      case 'Sick': return 'red';
      case 'Unpaid': return 'orange';
      case 'Other': return 'default';
      default: return 'default';
    }
  };

  const renderLeaveRequestCard = (record: LeaveRequest) => (
    <Card
      key={record.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(record)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Popconfirm
          key="delete"
          title={t('attendance:deleteConfirm')}
          description={t('attendance:deleteConfirmDescription')}
          onConfirm={() => handleDelete(record.id)}
          okText={t('common:actions.yes')}
          cancelText={t('common:actions.no')}
        >
          <Button
            type="link"
            icon={<DeleteOutlined />}
            danger
          >
            {t('common:actions.delete')}
          </Button>
        </Popconfirm>,
      ]}
    >
      <Card.Meta
        title={record.employee.name}
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getTypeColor(record.type)}>
                {t(`attendance:leaveTypes.${record.type.toLowerCase()}`)}
              </Tag>
              <Tag color={getStatusColor(record.status)}>
                {t(`attendance:leaveStatus.${record.status.toLowerCase()}`)}
              </Tag>
            </div>
            <div>
              <strong>{t('attendance:startDate')}:</strong> {
                record.startDate?.toDate ? 
                  dayjs(record.startDate.toDate()).format('DD MMM YYYY') :
                  dayjs(record.startDate).format('DD MMM YYYY')
              }
            </div>
            <div>
              <strong>{t('attendance:endDate')}:</strong> {
                record.endDate?.toDate ? 
                  dayjs(record.endDate.toDate()).format('DD MMM YYYY') :
                  dayjs(record.endDate).format('DD MMM YYYY')
              }
            </div>
            <div>
              <strong>{t('attendance:duration')}:</strong> {
                (() => {
                  const startDate = record.startDate?.toDate ? record.startDate.toDate() : record.startDate;
                  const endDate = record.endDate?.toDate ? record.endDate.toDate() : record.endDate;
                  const start = dayjs(startDate);
                  const end = dayjs(endDate);
                  const days = end.diff(start, 'day') + 1;
                  return `${days} ${days === 1 ? t('attendance:day') : t('attendance:days')}`;
                })()
              }
            </div>
            {record.reason && (
              <div>
                <strong>{t('attendance:reason')}:</strong> {record.reason}
              </div>
            )}
          </Space>
        }
      />
    </Card>
  );

  const handleCreate = () => {
    setSelectedLeaveRequest(null);
    setIsModalOpen(true);
  };

  const handleEdit = (record: LeaveRequest) => {
    setSelectedLeaveRequest(record);
    setIsModalOpen(true);
  };

  const handleView = (record: LeaveRequest) => {
    setSelectedLeaveRequest(record);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting leave request:', error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedLeaveRequest(null);
  };

  const columns: TableProps<LeaveRequest>['columns'] = [
    {
      title: t('employees:fields.name'),
      dataIndex: ['employee', 'name'],
      key: 'employeeName',
      width: 200,
      fixed: 'left' as const,
    },
    {
      title: t('attendance:leaveType'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: LeaveRequest['type']) => (
        <Tag color={getTypeColor(type)}>
          {t(`attendance:leaveTypes.${type.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t('attendance:startDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      render: (date: any) => {
        // Handle Firestore timestamp
        const dateValue = date?.toDate ? date.toDate() : date;
        return dayjs(dateValue).format('DD MMM YYYY');
      },
    },
    {
      title: t('attendance:endDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      render: (date: any) => {
        // Handle Firestore timestamp
        const dateValue = date?.toDate ? date.toDate() : date;
        return dayjs(dateValue).format('DD MMM YYYY');
      },
    },
    {
      title: t('attendance:duration'),
      key: 'duration',
      width: 100,
      render: (_, record) => {
        const startDate = record.startDate?.toDate ? record.startDate.toDate() : record.startDate;
        const endDate = record.endDate?.toDate ? record.endDate.toDate() : record.endDate;
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const days = end.diff(start, 'day') + 1;
        return `${days} ${days === 1 ? t('attendance:day') : t('attendance:days')}`;
      },
    },
    {
      title: t('common:statusLabel'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: LeaveRequest['status']) => (
        <Tag color={getStatusColor(status)}>
          {t(`attendance:leaveStatus.${status.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t('attendance:approvedBy'),
      dataIndex: ['approvedBy', 'name'],
      key: 'approvedBy',
      width: 150,
      render: (name: string, record) => {
        if (record.status === 'Pending') {
          return <span style={{ color: '#999' }}>--</span>;
        }
        return name || '--';
      },
    },
    {
      title: t('attendance:reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      ellipsis: true,
      render: (reason: string) => (
        <Tooltip title={reason}>
          <span>{reason}</span>
        </Tooltip>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
          >
            {t('common:actions.view')}
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            {t('common:actions.edit')}
          </Button>
          <Popconfirm
            title={t('attendance:deleteConfirm')}
            description={t('attendance:deleteConfirmDescription')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common:actions.yes')}
            cancelText={t('common:actions.no')}
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            >
              {t('common:actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Header */}
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>{t('attendance:leaveRequests')}</Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('attendance:createLeaveRequest')}
            </Button>
          </Col>
        </Row>

        {/* Search and Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Input.Search
              placeholder={t('common:search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              onSearch={() => setPagination(prev => ({ ...prev, current: 1 }))}
            />
          </Col>
          <Col xs={12} md={6}>
            <Select
              placeholder={t('common:statusLabel')}
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="pending">{t('attendance:leaveStatus.pending')}</Select.Option>
              <Select.Option value="approved">{t('attendance:leaveStatus.approved')}</Select.Option>
              <Select.Option value="rejected">{t('attendance:leaveStatus.rejected')}</Select.Option>
            </Select>
          </Col>
        </Row>

        {/* Table */}
        <Card>
          {screens.xs ? (
            <List
              dataSource={leaveRequestsData?.requests || []}
              renderItem={renderLeaveRequestCard}
              rowKey="id"
              loading={isLoading}
              grid={{ gutter: 16, xs: 1, sm: 1 }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={leaveRequestsData?.requests || []}
              rowKey="id"
              loading={isLoading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: leaveRequestsData?.total || 0,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  t('common:showingRange', { 
                    from: range[0], 
                    to: range[1], 
                    total 
                  }),
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ 
                    ...prev, 
                    current: page, 
                    pageSize: pageSize || prev.pageSize 
                  }));
                }
              }}
              scroll={{ x: 1200 }}
            />
          )}
        </Card>
      </Space>

      {/* Modal */}
      <LeaveRequestModal
        open={isModalOpen}
        editingRequest={selectedLeaveRequest}
        onCancel={handleModalClose}
        onSuccess={() => {
          handleModalClose();
          queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
        }}
      />
    </div>
  );
} 